import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '../lib/auth.js';
import { clearReturnTo, rememberReturnTo } from '../lib/returnTo.js';
import { BrandingProvider } from '../lib/branding.js';
import { CapabilitiesProvider, useCapabilities } from '../lib/capabilities.js';
import type { Permission, ServerCapabilities } from '../lib/types.js';
import { ToastProvider } from '../components/Toast.js';
import { UpdatePrompt } from '../components/UpdatePrompt.js';
import { Loading, EmptyState } from '../components/ui.js';
import { AppShell } from './AppShell.js';
import { LoginScreen } from '../screens/Login.js';
import { SetPasswordScreen } from '../screens/SetPassword.js';
import { ForgotPasswordScreen } from '../screens/ForgotPassword.js';
import { SignupScreen } from '../screens/Signup.js';
import { SetupScreen } from '../screens/Setup.js';
import { AuthCallbackScreen } from '../screens/AuthCallback.js';
import { BoxHomeScreen } from '../screens/BoxHome.js';
import { DashboardScreen } from '../screens/Dashboard.js';
import { ReportsScreen } from '../screens/Reports.js';
import { AlertsScreen } from '../screens/Alerts.js';
import { DisplaysScreen } from '../screens/Displays.js';
import { SosScreen } from '../screens/Sos.js';
import { ClaimDisplayScreen } from '../screens/ClaimDisplay.js';
import { DisplayGroupsScreen } from '../screens/DisplayGroups.js';
import { MediaLibraryScreen } from '../screens/MediaLibrary.js';
import { LayoutsScreen } from '../screens/Layouts.js';
import { LayoutEditorScreen } from '../screens/LayoutEditor.js';
import { MenuBoardsScreen } from '../screens/MenuBoards.js';
import { MenuBoardEditorScreen } from '../screens/MenuBoardEditor.js';
import { ScheduleScreen } from '../screens/Schedule.js';
import { UsersScreen } from '../screens/settings/Users.js';
import { RolesScreen } from '../screens/settings/Roles.js';
import { FoldersScreen } from '../screens/settings/Folders.js';
import { ApiTokensScreen } from '../screens/settings/ApiTokens.js';
import { WebhooksScreen } from '../screens/settings/Webhooks.js';
import { ImportScreen } from '../screens/settings/Import.js';
import { ModulesScreen } from '../screens/settings/Modules.js';
import { BrandingScreen } from '../screens/settings/Branding.js';
import { AuditLogScreen } from '../screens/settings/AuditLog.js';
import { SsoScreen } from '../screens/settings/Sso.js';
import { BackupScreen } from '../screens/settings/Backup.js';
import { BillingScreen } from '../screens/settings/Billing.js';
import { ImpersonateScreen } from '../screens/settings/Impersonate.js';
import { LicensesScreen } from '../screens/Licenses.js';

function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const { user, loading } = useAuth();
  const location = useLocation();
  const signedIn = Boolean(user);
  useEffect(() => {
    if (signedIn) clearReturnTo();
  }, [signedIn]);

  if (loading) return <Loading />;
  if (!user) {
    // A deep link opened while signed out (e.g. a scanned /claim QR) resumes after login.
    if (location.pathname !== '/') rememberReturnTo(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  return children;
}

function RequirePermission({ slug, children }: { slug: Permission; children: ReactNode }): ReactNode {
  const { can } = useAuth();
  if (!can(slug)) {
    return <EmptyState>You don’t have access to this area.</EmptyState>;
  }
  return children;
}

/**
 * Gates a whole route on a server capability (ADR-0019 is the first feature
 * needing this) — e.g. a self-hosted install with billing off. `null` (still
 * loading) renders nothing rather than a flash of "not available".
 */
function RequireCapability({ flag, children }: { flag: keyof ServerCapabilities; children: ReactNode }): ReactNode {
  const caps = useCapabilities();
  if (caps === null) return null;
  // A box has no such screens at all: send a typed or bookmarked URL home.
  if (caps.simple && !caps[flag]) return <Navigate to="/" replace />;
  if (!caps[flag]) return <EmptyState>This feature isn't available on this deployment.</EmptyState>;
  return children;
}

function Landing(): ReactNode {
  const { can } = useAuth();
  const caps = useCapabilities();
  if (caps === null) return null;
  if (caps.simple) return <BoxHomeScreen />;
  return <Navigate to={can('reports.view') ? '/dashboard' : '/displays'} replace />;
}

export function App(): ReactNode {
  return (
    <BrowserRouter>
      <BrandingProvider>
      <CapabilitiesProvider>
      <AuthProvider>
        <ToastProvider>
          <UpdatePrompt />
          <Routes>
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
            <Route path="/signup" element={<SignupScreen />} />
            <Route path="/setup" element={<SetupScreen />} />
            <Route path="/set-password" element={<SetPasswordScreen />} />
            <Route path="/auth/callback" element={<AuthCallbackScreen />} />
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<Landing />} />
              <Route
                path="/dashboard"
                element={
                  <RequirePermission slug="reports.view">
                    <DashboardScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/reports"
                element={
                  <RequirePermission slug="reports.view">
                    <ReportsScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/alerts"
                element={
                  <RequirePermission slug="alerts.view">
                    <AlertsScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/sos"
                element={
                  <RequirePermission slug="sos.view">
                    <SosScreen />
                  </RequirePermission>
                }
              />
              <Route path="/displays" element={<DisplaysScreen />} />
              <Route path="/fleet" element={<Navigate to="/displays" replace />} />
              <Route
                path="/claim"
                element={
                  <RequirePermission slug="displays.manage">
                    <ClaimDisplayScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/display-groups"
                element={
                  <RequirePermission slug="displays.view">
                    <DisplayGroupsScreen />
                  </RequirePermission>
                }
              />
              <Route path="/media" element={<MediaLibraryScreen />} />
              <Route path="/layouts" element={<LayoutsScreen />} />
              <Route path="/layouts/new" element={<LayoutEditorScreen />} />
              <Route path="/layouts/:id" element={<LayoutEditorScreen />} />
              <Route
                path="/menu-boards"
                element={
                  <RequirePermission slug="menuboards.view">
                    <MenuBoardsScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/menu-boards/:id"
                element={
                  <RequirePermission slug="menuboards.view">
                    <MenuBoardEditorScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/licenses"
                element={
                  <RequireCapability flag="portableLicenseSales">
                    <RequirePermission slug="displays.view">
                      <LicensesScreen />
                    </RequirePermission>
                  </RequireCapability>
                }
              />
              <Route path="/displays/:id/schedule" element={<ScheduleScreen />} />
              <Route
                path="/settings/users"
                element={
                  <RequirePermission slug="users.manage">
                    <UsersScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/settings/roles"
                element={
                  <RequireCapability flag="roles">
                    <RequirePermission slug="roles.manage">
                      <RolesScreen />
                    </RequirePermission>
                  </RequireCapability>
                }
              />
              <Route
                path="/settings/folders"
                element={
                  <RequirePermission slug="folders.manage">
                    <FoldersScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/settings/api-tokens"
                element={
                  <RequireCapability flag="apiTokens">
                    <RequirePermission slug="api.manage">
                      <ApiTokensScreen />
                    </RequirePermission>
                  </RequireCapability>
                }
              />
              <Route
                path="/settings/webhooks"
                element={
                  <RequireCapability flag="webhooks">
                    <RequirePermission slug="webhooks.view">
                      <WebhooksScreen />
                    </RequirePermission>
                  </RequireCapability>
                }
              />
              <Route
                path="/settings/import"
                element={
                  <RequirePermission slug="import.run">
                    <ImportScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/settings/modules"
                element={
                  <RequirePermission slug="modules.view">
                    <ModulesScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/settings/branding"
                element={
                  <RequireCapability flag="branding">
                    <RequirePermission slug="settings.manage">
                      <BrandingScreen />
                    </RequirePermission>
                  </RequireCapability>
                }
              />
              <Route
                path="/settings/audit"
                element={
                  <RequirePermission slug="audit.view">
                    <AuditLogScreen />
                  </RequirePermission>
                }
              />
              <Route
                path="/settings/sso"
                element={
                  <RequireCapability flag="sso">
                    <RequirePermission slug="sso.manage">
                      <SsoScreen />
                    </RequirePermission>
                  </RequireCapability>
                }
              />
              <Route
                path="/settings/billing"
                element={
                  <RequireCapability flag="billing">
                    <RequirePermission slug="billing.view">
                      <BillingScreen />
                    </RequirePermission>
                  </RequireCapability>
                }
              />
              <Route
                path="/settings/backup"
                element={
                  <RequireCapability flag="boxBackup">
                    <RequirePermission slug="box.backup">
                      <BackupScreen />
                    </RequirePermission>
                  </RequireCapability>
                }
              />
              <Route
                path="/settings/impersonate"
                element={
                  <RequireCapability flag="impersonation">
                    <RequirePermission slug="platform.impersonate">
                      <ImpersonateScreen />
                    </RequirePermission>
                  </RequireCapability>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/displays" replace />} />
          </Routes>
        </ToastProvider>
      </AuthProvider>
      </CapabilitiesProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}
